import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { movieService, watchPartyService } from '../../services/api';
import { Movie, WatchParty } from '../../types';
import './WatchParty.css';

// Extended WatchParty type with extra fields from API
interface ExtendedWatchParty extends WatchParty {
  movie_title?: string;
  thumbnail?: string;
  genre?: string;
  host_name?: string;
  seconds_empty?: number;
  has_active_participants?: number;
}

const WatchPartyPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [parties, setParties] = useState<ExtendedWatchParty[]>([]);
  const [movies, setMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [genreFilter, setGenreFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [movieSearchQuery, setMovieSearchQuery] = useState('');
  const [movieGenreFilter, setMovieGenreFilter] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const partiesPerPage = 10;
  
  // Create party modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMovieModal, setShowMovieModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingRoomCode, setPendingRoomCode] = useState<string | null>(null);
  
  // Form state
  const [partyTitle, setPartyTitle] = useState('');
  const [partyPassword, setPartyPassword] = useState('');
  const [showPartyPassword, setShowPartyPassword] = useState(false);
  const [showJoinPassword, setShowJoinPassword] = useState(false);
  const [maxParticipants, setMaxParticipants] = useState(6);
  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null);
  const [joinPassword, setJoinPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [creating, setCreating] = useState(false);

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/watchparty');
    }
  }, [isAuthenticated, navigate]);

  // Load parties
  const loadParties = useCallback(async () => {
    try {
      const data = await watchPartyService.getActiveParties(1);
      const partyList = Array.isArray(data) ? data : (data as any)?.data || [];
      setParties(partyList);
    } catch (error) {
      console.error('Failed to load parties:', error);
      setParties([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load movies for selection
  const loadMovies = useCallback(async () => {
    try {
      // Load multiple pages of movies
      let allMovies: Movie[] = [];
      for (let page = 1; page <= 5; page++) {
        const response: any = await movieService.getMovies(page, {});
        const movieList = Array.isArray(response) ? response : response?.data || [];
        allMovies = [...allMovies, ...movieList];
        if (movieList.length < 12) break;
      }
      setMovies(allMovies);
    } catch (error) {
      console.error('Failed to load movies:', error);
    }
  }, []);

  useEffect(() => {
    loadParties();
    loadMovies();
    
    // Auto-refresh parties every 10 seconds
    const interval = setInterval(loadParties, 10000);
    return () => clearInterval(interval);
  }, [loadParties, loadMovies]);

  // Filter parties
  const filteredParties = parties.filter(party => {
    const matchesSearch = !searchQuery || 
      party.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      party.room_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (party.movie_title && party.movie_title.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesGenre = !genreFilter || 
      (party.genre && party.genre.toLowerCase().includes(genreFilter.toLowerCase())) ||
      (party.movie?.genre && party.movie.genre.toLowerCase().includes(genreFilter.toLowerCase()));
    
    const matchesStatus = !statusFilter || party.status === statusFilter;
    
    return matchesSearch && matchesGenre && matchesStatus;
  });
  
  // Paginated parties
  const totalPages = Math.ceil(filteredParties.length / partiesPerPage);
  const startIndex = (currentPage - 1) * partiesPerPage;
  const paginatedParties = filteredParties.slice(startIndex, startIndex + partiesPerPage);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, genreFilter, statusFilter]);
  
  // Filter movies
  const filteredMovies = movies.filter(movie => {
    const matchesSearch = !movieSearchQuery ||
      movie.title.toLowerCase().includes(movieSearchQuery.toLowerCase());
    
    const matchesGenre = !movieGenreFilter ||
      (movie.genre && movie.genre.toLowerCase().includes(movieGenreFilter.toLowerCase()));
    
    return matchesSearch && matchesGenre;
  });

  // Get thumbnail URL
  const getThumbnailUrl = (thumbnail?: string) => {
    if (!thumbnail || thumbnail === 'null' || thumbnail === '') {
      return '/public/assets/images/logoformovies.png';
    }
    if (thumbnail.startsWith('http')) return thumbnail;
    return `/public/uploads/thumbnails/${thumbnail}`;
  };
  
  // Generate cinema seats HTML
  const renderSeats = (current: number, max: number) => {
    const seats = [];
    for (let i = 1; i <= max; i++) {
      const isFilled = i <= current;
      seats.push(
        <i 
          key={i} 
          className={`fas fa-couch text-lg transition-colors ${isFilled ? 'text-netflix-red' : 'text-green-500'}`}
        />
      );
    }
    return seats;
  };

  // Create party handler
  const handleCreateParty = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!partyTitle.trim()) {
      alert('Please enter a party name');
      return;
    }
    
    if (!partyPassword.trim()) {
      alert('Please enter a password');
      return;
    }

    try {
      setCreating(true);
      const party = await watchPartyService.createParty(
        selectedMovie?.id || 0,
        partyTitle,
        maxParticipants,
        partyPassword
      );
      
      setShowCreateModal(false);
      navigate(`/room/${party.room_code}`);
    } catch (error: any) {
      console.error('Failed to create party:', error);
      alert('Failed to create party: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  // Join party handler
  const handleJoinParty = (party: WatchParty) => {
    const isProtected = party.password && party.password !== '';
    
    if (isProtected) {
      setPendingRoomCode(party.room_code);
      setJoinPassword('');
      setPasswordError('');
      setShowPasswordModal(true);
    } else {
      navigate(`/room/${party.room_code}`);
    }
  };

  // Submit password handler
  const handleSubmitPassword = async () => {
    if (!joinPassword.trim()) {
      setPasswordError('Please enter a password');
      return;
    }

    try {
      // Store password and navigate
      localStorage.setItem(`party_password_${pendingRoomCode}`, joinPassword);
      setShowPasswordModal(false);
      navigate(`/room/${pendingRoomCode}`);
    } catch (error: any) {
      setPasswordError(error.message || 'Failed to join party');
    }
  };

  // Copy room link
  const copyRoomLink = (roomCode: string) => {
    const url = `${window.location.origin}/room/${roomCode}`;
    navigator.clipboard.writeText(url).then(() => {
      alert(`Link copied: ${roomCode}`);
    }).catch(() => {
      alert('Failed to copy link');
    });
  };

  // Reset create form
  const resetCreateForm = () => {
    setPartyTitle('');
    setPartyPassword('');
    setMaxParticipants(6);
    setSelectedMovie(null);
  };

  const genres = ['Action', 'Comedy', 'Drama', 'Horror', 'Sci-Fi', 'Romance', 'Thriller', 'Animation'];

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-netflix-darker pt-20">
      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl lg:text-5xl font-black mb-4 bg-gradient-to-r from-netflix-red to-red-600 bg-clip-text text-transparent">
            <i className="fas fa-users text-netflix-red"></i> Watch Party
          </h1>
          <p className="text-netflix-light-gray text-lg">Watch movies together with friends in real-time</p>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-4 lg:p-8 border border-white/10 shadow-2xl backdrop-blur-sm">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-center gap-3 mb-4 pb-4 border-b border-white/10">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <i className="fas fa-list text-green-400 text-2xl"></i>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-2xl font-bold">Active Parties</h2>
                <p className="text-sm text-gray-400">Join an existing watch party or create a new one</p>
              </div>
              <button
                onClick={() => { resetCreateForm(); setShowCreateModal(true); }}
                className="px-4 py-2 bg-gradient-to-r from-netflix-red to-red-600 hover:from-red-600 hover:to-netflix-red rounded-lg font-bold transition-all flex items-center gap-2"
              >
                <i className="fas fa-plus-circle"></i>
                <span>Create</span>
              </button>
              <button
                onClick={loadParties}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Refresh"
              >
                <i className="fas fa-sync-alt text-gray-400 hover:text-white"></i>
              </button>
            </div>

            {/* Filters */}
            <div className="mb-4 pb-4 border-b border-white/10">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Search by party name or room code..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2.5 pl-10 bg-black/40 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-netflix-red text-white placeholder-gray-500 text-sm"
                  />
                  <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                </div>
                <select
                  value={genreFilter}
                  onChange={(e) => setGenreFilter(e.target.value)}
                  className="px-4 py-2.5 bg-black/40 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-netflix-red text-white text-sm"
                >
                  <option value="">All Genres</option>
                  {genres.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2.5 bg-black/40 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-netflix-red text-white text-sm"
                >
                  <option value="">All Status</option>
                  <option value="waiting">Waiting</option>
                  <option value="active">Active</option>
                </select>
              </div>
            </div>

            {/* Parties List */}
            <div className="space-y-2 max-h-[600px] overflow-y-auto custom-scrollbar pt-4">
              {loading ? (
                <div className="flex flex-col justify-center items-center py-16">
                  <div className="relative mb-6">
                    <img src="/public/assets/images/logoformovies.png" alt="CineClubs" className="w-32 h-32 object-contain relative z-10" />
                    <div className="progress-ring"></div>
                    <div className="neon-glow"></div>
                  </div>
                  <p className="text-white text-lg font-bold loading-text">Loading active parties...</p>
                </div>
              ) : paginatedParties.length === 0 ? (
                <div className="text-center py-12 bg-white/5 rounded-xl border border-white/10">
                  <div className="w-16 h-16 bg-netflix-red/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-users text-3xl text-netflix-red"></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">No Parties Found</h3>
                  <p className="text-gray-400 mb-4 text-sm">Try adjusting your filters or create a new party!</p>
                  <button
                    onClick={() => { resetCreateForm(); setShowCreateModal(true); }}
                    className="px-5 py-2.5 bg-netflix-red hover:bg-red-700 rounded-lg font-bold transition-all text-sm"
                  >
                    <i className="fas fa-plus-circle mr-2"></i>Create Party
                  </button>
                </div>
              ) : (
                paginatedParties.map(party => {
                  const movieTitle = party.movie_title || party.movie?.title || 'No Movie Selected';
                  const thumbnail = party.thumbnail || party.movie?.thumbnail;
                  const currentParticipants = party.current_participants || 1;
                  const maxParticipants = party.max_participants || 6;
                  const isProtected = party.password && party.password !== '';
                  
                  // Inactivity timer calculation
                  const secondsEmpty = party.seconds_empty || 0;
                  const hasActiveParticipants = party.has_active_participants || 0;
                  const EMPTY_THRESHOLD = 300; // 5 minutes
                  const secondsUntilDeletion = EMPTY_THRESHOLD - secondsEmpty;
                  const showInactivityWarning = hasActiveParticipants === 0 && secondsEmpty > 0 && secondsUntilDeletion > 0;
                  
                  return (
                    <div
                      key={party.id}
                      className={`party-card bg-gradient-to-br from-white/10 to-white/5 rounded-xl p-2.5 sm:p-4 border border-white/20 backdrop-blur-sm relative w-full sm:max-w-4xl sm:mx-auto ${isProtected ? 'party-protected' : ''}`}
                      data-room-code={party.room_code}
                      data-protected={isProtected}
                    >
                      {/* Inactivity Warning Timer */}
                      {showInactivityWarning && (
                        <div className="absolute top-2 right-2 bg-yellow-500/90 text-black px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 shadow-lg animate-pulse z-10">
                          <i className="fas fa-clock"></i>
                          <span>Deleting in {Math.floor(secondsUntilDeletion / 60)}:{(secondsUntilDeletion % 60).toString().padStart(2, '0')}</span>
                        </div>
                      )}
                      
                      {/* Mobile Layout */}
                      <div className="flex sm:hidden gap-2.5 w-full">
                        <div className="relative group flex-shrink-0">
                          <img
                            src={getThumbnailUrl(thumbnail)}
                            alt={movieTitle}
                            className="w-14 h-14 object-cover rounded-lg shadow-lg"
                            onError={(e) => { e.currentTarget.src = '/public/assets/images/logoformovies.png'; }}
                          />
                          {isProtected && (
                            <div className="absolute -top-1 -right-1 bg-yellow-500 rounded-full w-4 h-4 flex items-center justify-center shadow-lg">
                              <i className="fas fa-lock text-black text-[8px]"></i>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-xs mb-0.5 truncate text-white">{party.title}</h3>
                          <p className="text-[9px] text-gray-400 mb-1 truncate">{movieTitle}</p>
                          <div className="flex items-center gap-1 mb-1">
                            {renderSeats(currentParticipants, maxParticipants)}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <code className="text-netflix-red font-mono font-bold text-[9px]">{party.room_code}</code>
                            <span className="bg-green-500/20 text-green-400 px-1 py-0.5 rounded text-[8px] font-semibold flex items-center gap-0.5">
                              <i className="fas fa-circle text-[5px] animate-pulse"></i>
                              {party.status === 'waiting' ? 'Wait' : 'Live'}
                            </span>
                            <span className="bg-netflix-red text-white px-1.5 py-0.5 rounded text-[9px] font-bold">
                              {currentParticipants}/{maxParticipants}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Mobile Buttons */}
                      <div className="flex sm:hidden gap-2 mt-2 pt-2 border-t border-white/10">
                        <button
                          onClick={() => copyRoomLink(party.room_code)}
                          className="flex-1 py-2 bg-white/10 active:bg-white/20 rounded-lg transition-all font-semibold text-xs flex items-center justify-center gap-1.5"
                        >
                          <i className="fas fa-copy text-xs"></i>
                          <span>Copy</span>
                        </button>
                        <button
                          onClick={() => handleJoinParty(party)}
                          className="flex-1 py-2 bg-gradient-to-r from-netflix-red to-red-600 active:from-red-700 active:to-red-800 rounded-lg font-bold transition-all text-xs flex items-center justify-center gap-1.5 shadow-lg shadow-netflix-red/30"
                        >
                          <i className="fas fa-sign-in-alt text-xs"></i>
                          <span>Join Party</span>
                        </button>
                      </div>
                      
                      {/* Desktop Layout */}
                      <div className="hidden sm:flex items-center gap-3">
                        <div className="relative group flex-shrink-0">
                          <img
                            src={getThumbnailUrl(thumbnail)}
                            alt={movieTitle}
                            className="w-24 h-24 object-cover rounded-lg shadow-lg transition-transform duration-300 group-hover:scale-105"
                            onError={(e) => { e.currentTarget.src = '/public/assets/images/logoformovies.png'; }}
                          />
                          {isProtected && (
                            <div className="absolute -top-1 -right-1 bg-yellow-500 rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
                              <i className="fas fa-lock text-black text-[10px]"></i>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-sm mb-0.5 truncate text-white">{party.title}</h3>
                          <p className="text-[10px] text-gray-400 mb-2 truncate">{movieTitle}</p>
                          <div className="flex items-center gap-1.5 mb-2">
                            {renderSeats(currentParticipants, maxParticipants)}
                          </div>
                          <div className="flex items-center gap-2">
                            <code className="text-netflix-red font-mono font-bold text-[10px]">{party.room_code}</code>
                            <span className="bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded text-[9px] font-semibold flex items-center gap-0.5">
                              <i className="fas fa-circle text-[6px] animate-pulse"></i>
                              {party.status === 'waiting' ? 'Waiting' : 'Active'}
                            </span>
                            <span className="bg-netflix-red text-white px-2 py-0.5 rounded text-[10px] font-bold">
                              {currentParticipants}/{maxParticipants}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1.5 flex-shrink-0">
                          <button
                            onClick={() => copyRoomLink(party.room_code)}
                            className="p-1.5 bg-white/10 hover:bg-white/20 hover:shadow-[0_0_10px_rgba(255,255,255,0.3)] rounded-lg transition-all"
                            title="Copy Room Link"
                          >
                            <i className="fas fa-copy text-gray-300 text-xs"></i>
                          </button>
                          <button
                            onClick={() => handleJoinParty(party)}
                            className="px-3 py-1.5 bg-gradient-to-r from-netflix-red to-red-600 hover:from-red-600 hover:to-netflix-red hover:shadow-[0_0_15px_rgba(229,9,20,0.6)] rounded-lg font-bold transition-all text-[11px] shadow-lg shadow-netflix-red/30"
                          >
                            Join
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between pt-4 border-t border-white/10 mt-4">
              <span className="text-sm text-gray-400">
                Showing {paginatedParties.length} of {filteredParties.length} parties
              </span>
              {totalPages > 1 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                    .map((page, index, arr) => (
                      <React.Fragment key={page}>
                        {index > 0 && arr[index - 1] !== page - 1 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1.5 rounded-lg transition-all text-sm font-semibold ${
                            page === currentPage 
                              ? 'bg-netflix-red text-white' 
                              : 'bg-white/10 hover:bg-white/20'
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))
                  }
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="mt-12 bg-gradient-to-r from-white/5 to-transparent rounded-2xl p-6 border border-white/10 max-w-6xl mx-auto">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <i className="fas fa-info-circle text-blue-400"></i>
            How Watch Party Works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex gap-3">
              <div className="w-10 h-10 bg-netflix-red/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-netflix-red font-bold">1</span>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Create or Join</h4>
                <p className="text-sm text-gray-400">Create a new party or join an existing one using the room code</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-10 h-10 bg-netflix-red/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-netflix-red font-bold">2</span>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Invite Friends</h4>
                <p className="text-sm text-gray-400">Share the room code with your friends to watch together</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-10 h-10 bg-netflix-red/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-netflix-red font-bold">3</span>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Watch & Chat</h4>
                <p className="text-sm text-gray-400">Enjoy synchronized playback and real-time chat</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Party Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-netflix-darker rounded-2xl max-w-2xl w-full border border-netflix-red shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-netflix-red to-red-600 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <i className="fas fa-plus-circle text-2xl"></i>
                Create New Party
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="text-white/80 hover:text-white text-2xl">
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <form onSubmit={handleCreateParty} className="space-y-5">
                {/* Movie Selection */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-white mb-2">
                    <i className="fas fa-film text-netflix-red"></i>
                    Choose Movie <span className="text-xs text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowMovieModal(true)}
                    className="w-full px-4 py-3 bg-black/40 border border-white/20 rounded-xl text-left flex items-center justify-between group hover:border-white/40"
                  >
                    <span className={selectedMovie ? 'text-white font-semibold' : 'text-gray-400'}>
                      {selectedMovie ? selectedMovie.title : 'Click to select a movie...'}
                    </span>
                    <i className="fas fa-search text-netflix-red opacity-50 group-hover:opacity-100"></i>
                  </button>
                </div>

                {/* Party Title */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-white mb-2">
                    <i className="fas fa-heading text-netflix-red"></i>
                    Party Name <span className="text-netflix-red">*</span>
                  </label>
                  <input
                    type="text"
                    value={partyTitle}
                    onChange={(e) => setPartyTitle(e.target.value)}
                    className="w-full px-4 py-3 bg-black/40 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-netflix-red text-white placeholder-gray-500"
                    placeholder="e.g., Friday Night Movie with Friends"
                    required
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-white mb-2">
                    <i className="fas fa-lock text-netflix-red"></i>
                    Password Protection <span className="text-netflix-red">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPartyPassword ? 'text' : 'password'}
                      value={partyPassword}
                      onChange={(e) => setPartyPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-12 bg-black/40 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-netflix-red text-white placeholder-gray-500 transition-all hover:border-white/40"
                      placeholder="Enter a secure password"
                      maxLength={50}
                      required
                    />
                    <button 
                      type="button"
                      onClick={() => setShowPartyPassword(!showPartyPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      <i className={`fas ${showPartyPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    <i className="fas fa-shield-alt"></i> Password is required for all parties
                  </p>
                </div>

                {/* Max Participants */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-white mb-2">
                    <i className="fas fa-users text-netflix-red"></i>
                    Max Participants
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      value={maxParticipants}
                      onChange={(e) => setMaxParticipants(parseInt(e.target.value))}
                      min="2"
                      max="6"
                      className="flex-1 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-netflix-red"
                    />
                    <div className="w-16 text-center">
                      <span className="text-2xl font-bold text-netflix-red">{maxParticipants}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Slide to set maximum number of participants (2-6)</p>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={creating}
                  className="w-full py-4 bg-gradient-to-r from-netflix-red to-red-600 hover:from-red-600 hover:to-netflix-red rounded-xl text-lg font-bold transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                >
                  {creating ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-video"></i>
                      Create Watch Party
                      <i className="fas fa-arrow-right"></i>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Movie Selection Modal */}
      {showMovieModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-netflix-darker rounded-2xl max-w-4xl w-full border border-netflix-red shadow-2xl shadow-netflix-red/50 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-netflix-red to-red-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <i className="fas fa-film text-2xl"></i>
                Select Movie
              </h2>
              <button onClick={() => setShowMovieModal(false)} className="text-white/80 hover:text-white text-2xl transition-colors">
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            {/* Search and Filter Bar */}
            <div className="p-4 border-b border-white/10 bg-black/40 flex-shrink-0">
              <div className="flex gap-3 mb-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Search movies..."
                    value={movieSearchQuery}
                    onChange={(e) => setMovieSearchQuery(e.target.value)}
                    className="w-full px-4 py-2.5 pl-10 bg-black/60 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-netflix-red text-white placeholder-gray-500"
                  />
                  <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                </div>
                <button
                  onClick={() => { setSelectedMovie(null); setMovieSearchQuery(''); setMovieGenreFilter(''); }}
                  className="px-4 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-sm font-semibold"
                >
                  <i className="fas fa-times-circle mr-1"></i> Clear
                </button>
              </div>
              
              {/* Genre Filters */}
              <div className="flex gap-2 flex-wrap">
                {['', 'Action', 'Comedy', 'Drama', 'Horror', 'Sci-Fi', 'Romance', 'Thriller'].map(genre => (
                  <button
                    key={genre || 'all'}
                    onClick={() => setMovieGenreFilter(genre)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${
                      movieGenreFilter === genre
                        ? 'bg-netflix-red text-white'
                        : 'bg-white/10 hover:bg-white/20 text-white'
                    }`}
                  >
                    {genre || 'All'}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Movies Grid */}
            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {filteredMovies.length === 0 ? (
                  <div className="col-span-full text-center py-12">
                    <i className="fas fa-film text-6xl text-gray-600 mb-4"></i>
                    <p className="text-gray-400 text-lg">No movies found</p>
                  </div>
                ) : (
                  filteredMovies.map(movie => (
                    <div
                      key={movie.id}
                      onClick={() => {
                        setSelectedMovie(movie);
                        setShowMovieModal(false);
                        setMovieSearchQuery('');
                        setMovieGenreFilter('');
                      }}
                      className="movie-card-modal group cursor-pointer transition-transform hover:scale-[1.02]"
                    >
                      <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-black/40 border border-white/10 group-hover:border-netflix-red transition-all">
                        <img
                          src={getThumbnailUrl(movie.thumbnail)}
                          alt={movie.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.src = '/public/assets/images/no-image.jpg'; }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-all flex items-center justify-center">
                          <i className="fas fa-check-circle text-white text-3xl opacity-0 group-hover:opacity-100 transition-all transform scale-50 group-hover:scale-100"></i>
                        </div>
                      </div>
                      <div className="mt-2">
                        <h3 className="font-semibold text-sm text-white truncate group-hover:text-netflix-red transition-colors">{movie.title}</h3>
                        <p className="text-xs text-gray-400 truncate">{movie.release_year || movie.year || 'N/A'} • {movie.genre || 'Movie'}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <i className="fas fa-star text-yellow-400 text-xs"></i>
                          <span className="text-xs text-gray-400">{movie.rating || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Selected Movie Preview */}
            {selectedMovie && (
              <div className="border-t border-white/10 bg-black/40 p-4 flex-shrink-0">
                <div className="flex items-center gap-4">
                  <img 
                    src={getThumbnailUrl(selectedMovie.thumbnail)}
                    alt={selectedMovie.title}
                    className="w-16 h-24 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-1">{selectedMovie.title}</h3>
                    <p className="text-sm text-gray-400">{selectedMovie.release_year || selectedMovie.year} • {selectedMovie.genre} • ⭐ {selectedMovie.rating || 'N/A'}</p>
                  </div>
                  <button 
                    onClick={() => setShowMovieModal(false)}
                    className="px-6 py-3 bg-gradient-to-r from-netflix-red to-red-600 hover:from-red-600 hover:to-netflix-red rounded-lg font-bold transition-all transform hover:scale-105"
                  >
                    <i className="fas fa-check mr-2"></i>Confirm Selection
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-netflix-darker rounded-2xl max-w-md w-full border border-netflix-red shadow-2xl shadow-netflix-red/50">
            <div className="bg-gradient-to-r from-netflix-red to-red-600 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <i className="fas fa-lock text-2xl"></i>
                Password Required
              </h2>
              <button onClick={() => setShowPasswordModal(false)} className="text-white/80 hover:text-white text-2xl">
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-gray-300 mb-4">This party is password protected. Please enter the password to join.</p>
              
              {passwordError && (
                <div className="bg-red-500/20 border border-red-500 text-red-400 p-3 rounded-lg mb-4 flex items-center gap-2">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>{passwordError}</span>
                </div>
              )}
              
              <div className="space-y-4">
                <div className="relative">
                  <input
                    type={showJoinPassword ? 'text' : 'password'}
                    value={joinPassword}
                    onChange={(e) => setJoinPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 bg-black/40 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-netflix-red text-white placeholder-gray-500"
                    placeholder="Enter password"
                    onKeyPress={(e) => e.key === 'Enter' && handleSubmitPassword()}
                    autoFocus
                  />
                  <button 
                    type="button"
                    onClick={() => setShowJoinPassword(!showJoinPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    <i className={`fas ${showJoinPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPasswordModal(false)}
                    className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-bold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitPassword}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-netflix-red to-red-600 hover:from-red-600 hover:to-netflix-red rounded-xl font-bold transition-all transform hover:scale-105"
                  >
                    Join Party
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WatchPartyPage;
